import { Component, Input, OnInit } from '@angular/core';
import { CommunicationChannel, CommunicationMechanism, CommunicationMessage, CommunicationNotification, MessageHistoryItem } from '../model/login-response.dto';
import { Reservation, ReservationStatus } from '../model/reservation';
import { select, Store } from '@ngrx/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import * as AppSelectors from '../store/selectors/app.selectors';
import { CommunicationApiService } from '../apis/communication-api.service';
import * as AppActions from '../store/actions/app.actions';
import { TranslateService } from '@ngx-translate/core';
import { Preferences } from '@capacitor/preferences';
import { LocalStorageService } from '../lib/local-storage.service';
import { UtilsService } from '../lib/utils.service';
import { threadId } from 'worker_threads';
declare let $: any;

@Component({
  selector: 'app-communication-modal',
  templateUrl: './communication-modal.component.html',
  styleUrls: ['./communication-modal.component.scss']
})
export class CommunicationModalComponent implements OnInit {

  @Input() reservation: Reservation | null = null;
  
  sender: string = "";
  recepient: string | null = "";  
  
  channel: CommunicationChannel | null = null;
  message: CommunicationMessage | null = null;
  lang: string = "en";

  uiLang: string = "el";
  
  communication: CommunicationMechanism | null = null;
  communication$: Observable<CommunicationMechanism | null> = this.store.pipe(select(AppSelectors.selectCommunication));
  minTelephoneLength$: Observable<number> = this.store.pipe(select(AppSelectors.selectMinTelephoneLength));
  minTelephoneLength: number = 0;
  supportedChannels: CommunicationChannel[] = [];
  messages: CommunicationMessage[] = [];
  historyItems: MessageHistoryItem[] = [];

  sendToSource: boolean = false;
  hasPhone: boolean = false;
  hasMail: boolean = false;
  hasSourcePhone: boolean = false;
  hasSourceMail: boolean = false;

  historyMode: boolean = false;

  body: string = "";
  header: string = "";

  creditCardModeManual: boolean = false;
  creditCardCharge: number | null = null;
  
  destroy$: Subject<boolean> = new Subject<boolean>();

  readonly modalId: string = "modal-message";

  constructor(private store: Store<{ reservations: Reservation[] }>,
    private api: CommunicationApiService,
    private localStorageService: LocalStorageService,
    private utilsService: UtilsService,
    private translate: TranslateService) { }

  ngOnInit(): void {   
    
  }
 
  async refresh() {
    this.uiLang = this.translate.currentLang;

    this.recepient = this.reservation?.customerDetails?.mail || "";
    
    this.hasPhone = this.reservation?.customerDetails?.telephone != null && this.reservation.customerDetails.telephone.trim().length > 0;
    this.hasMail = this.reservation?.customerDetails?.mail != null && this.reservation.customerDetails.mail.trim().length > 0;
    this.hasSourcePhone = this.reservation?.sourceDetails?.contactTelephone != null && this.reservation.sourceDetails.contactTelephone.trim().length > 0;
    this.hasSourceMail = this.reservation?.sourceDetails?.contactMail != null && this.reservation.sourceDetails.contactMail.trim().length > 0;
    
    this.sendToSource = this.hasPhone == false && this.hasMail == false &&
      (this.hasSourcePhone === true || this.hasSourceMail === true);
    
    this.autoSelectLanguage();
    
    this.sender = await this.getSmsSender();
    
    this.communication$.pipe(takeUntil(this.destroy$)).subscribe((com) => {
      this.communication = com;      
      this.supportedChannels = this.communication?.communicationChannels?.filter(x => this.channelIsSupported(x)) || [];      
      this.selectChannel(this.supportedChannels[0]);       
    });    
    
    this.minTelephoneLength$.pipe(takeUntil(this.destroy$)).subscribe((minTelephoneLength) => {
      this.minTelephoneLength = minTelephoneLength;      
    });    
  }

  autoSelectLanguage() {
    if ((this.sendToSource === true && this.hasSourcePhone === true)) {      
      this.lang = this.isPhoneNumberGreek(this.reservation?.sourceDetails?.contactTelephone) === true                  
        ? "el"
        : "en";
    }
    else if (this.sendToSource === false && this.hasPhone === true) {
      this.lang = this.isPhoneNumberGreek(this.reservation?.customerDetails?.telephone) === true      
        ? "el"
        : "en";
    }
    else {
      this.lang = "en";
    }
  }

  private isPhoneNumberGreek(phone: string | null | undefined): boolean {
    if (phone == null || phone.trim() == "") return false;
    
    const hasGreekCountryCode = phone.indexOf("+30") == 0 || phone.indexOf("0030") == 0;
    const hasNoCountryCode = phone.indexOf("+") != 0 && phone.indexOf("00") != 0;

    return hasGreekCountryCode === true || hasNoCountryCode === true;
  }

  toggleSendToSource(sendToSource: boolean) {
    this.sendToSource = sendToSource;  
    this.updateRecipient();
  }

  selectChannel(channel: CommunicationChannel) {    
    this.channel = channel;    
    this.messages = [];

    if (this.channel == null) return;

    this.channel.displayLists.filter(x => x.value == "RESERVATIONS" || x.value == "ALL").map(x => this.messages.push(...x.communicationMessages));
    
    if (this.channel.value == "SMS") {     
      this.messages.push({
        content_el: { body: "", header: "" },
        content_en: { body: "", header: "" },
        messageId: "EMPTY",
        name_el: "Κενό Μήνυμα",
        name_en: "Blank Message",
        value: "OTHER",
        rules: []
      });
    }

    this.orderMessagesByStatus();

    this.selectMessage(this.messages[0]);

    this.updateRecipient();
  }

  orderMessagesByStatus() {    
    const rsvStatus = this.reservation?.reservationDetails.reservationsStatus; 
    
    const statusMatches = (a: string, b:ReservationStatus | undefined):boolean => {
      return a != null && b != null && a.toLowerCase() == b.toLowerCase();
    };

    let messagesWithoutRule = this.messages.filter(x => x.rules == null || x.rules.length == 0);
    let messagesNotForThisStatus = this.messages.filter(x => x.rules != null &&
                                                        x.rules.length > 0 &&
                                                        !x.rules.some(r => r != null && statusMatches(r.status, rsvStatus)));
    
    let messagesForThisStatus = this.messages.filter(x => x.rules != null &&
                                                          x.rules.length > 0 &&
                                                          x.rules.some(r => r != null && statusMatches(r.status, rsvStatus))).sort((a, b) => {      
      const correspondingRuleForStatusA = a.rules.find(r => statusMatches(r.status, rsvStatus))?.order || 9999;
      const correspondingRuleForStatusB = b.rules.find(r => statusMatches(r.status, rsvStatus))?.order || 9999;
                                                            
      return correspondingRuleForStatusA > correspondingRuleForStatusB ? 1 : -1;
    });

    this.messages = [
      ...messagesForThisStatus,
      ...messagesNotForThisStatus,
      ...messagesWithoutRule
    ];
  }

  updateRecipient() {
    const mailToUse = this.sendToSource === true
    ? (this.reservation?.sourceDetails?.contactMail || "")
    : (this.reservation?.customerDetails.mail || "");

    const phoneToUse = this.sendToSource === true
      ? (this.reservation?.sourceDetails?.contactTelephone || "")
      : (this.reservation?.customerDetails.telephone || "");
    
    this.recepient = this.channel?.value == 'EMAIL'
      ? mailToUse
      : phoneToUse;
  }

  selectMessage(msg: CommunicationMessage | null) {
    this.message = msg;
    this.creditCardModeManual = false;

    if (this.message == null) return;

    this.body = this.lang == 'el'
      ? this.message.content_el?.body
      : this.message.content_en?.body;
    
    this.header = this.lang == 'el'
      ? this.message.content_el?.header
      : this.message.content_en?.header
  }

  selectLang(lng: string) {
    this.lang = lng;
    this.selectMessage(this.message);
  }

  show() {     
    $('#' + this.modalId).modal("show");    
    this.refresh();
  }

  closeModal() {    
    $('#' + this.modalId).modal("hide");    
  }

  private channelIsSupported(channel: CommunicationChannel): boolean {   
    if (channel == null) return false;

    if (channel.displayLists == null) {
      channel.displayLists = [];
    }
    
    if (channel.value == "EMAIL" && channel.displayLists.length == 0) return false; // empty msg is always added to SMS!
    
    if (channel.value == "EMAIL" && this.hasMail == false && this.hasSourceMail == false) return false;    
    if (channel.value == "SMS" && this.hasPhone == false && this.hasSourcePhone == false) return false;
    
    return true;

  }

  ngOnDestroy() {    
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  sendNow() {
    this.send(new Date());
  }

  sendLater() {
    //todo
  }

  send(whenToSend: Date) {
    const formError = this.getFormError();

    if (formError != null) {
      this.store.dispatch({ type: AppActions.showAlert.type, alert: formError });   
      return;
    }
 
    let notification: CommunicationNotification = {
      communicationChannelId: this.channel?.id || "",
      reservationId: this.reservation?.reservationDetails.reservationId || "",      
    };

    notification.message = {
      body: this.body,
      header: this.header,
      countryCode: "+30", // todo!
      language: this.lang,
      recipient: this.recepient || "",
      sender: this.sender.trim(),
      toBeSentAt: whenToSend.getTime()
    };

    if (this.creditCardModeManual === true) {
      notification.message.chargeAmount = btoa(encodeURIComponent(this.creditCardCharge?.toString() || ""));
    }
        
    this.api.sendMessage(this.reservation?.reservationDetails.restaurantId || "", notification).subscribe({
      next: async response => {
        console.log(response);
        
        if(response.Status === true) {
          this.store.dispatch({ type: AppActions.showAlert.type, alert: this.translate.instant('communication.success') });      
          
          if (this.channel?.value == "SMS" && this.sender != null && this.sender.trim() != "") {
            await this.saveSmsSender();
          }
        } 
        else {
           this.store.dispatch({ type: AppActions.showAlert.type, alert: this.translate.instant('communication.error') });
        }        
      },
      error: () => {
        this.store.dispatch({ type: AppActions.showAlert.type, alert: this.translate.instant('communication.error') });
      },
      complete: () => {
        this.closeModal();
      }
    });
  }

  async saveSmsSender() {    
    await Preferences.set({
      key: 'smsSender',
      value: this.sender.trim()
    });    
  }

  async getSmsSender(): Promise<string> {    
    const { value } = await Preferences.get({ key: 'smsSender' });    

    return value || "";
  }

  getHistory() {
    this.api.getNotifications(
      this.reservation?.reservationDetails.restaurantId || "",
      this.reservation?.reservationDetails.reservationId || "").subscribe(data => {
        this.historyItems = data.messages.sort((a, b) => b.toBeSentAt - a.toBeSentAt);
    });
  }

  getFormError(): string | null {
    const lang = this.translate.currentLang;

    if (this.channel?.value == "SMS") { // SMS
      if (this.recepient == null ||
          this.recepient.trim().length < this.minTelephoneLength ||
          /^[+]*[0-9]*$/g.test(this.recepient.trim()) == false) {
        return lang == "el"       
            ? "O αριθμός τηλεφώνου είναι λάθος."
            : "Telephone number is wrong."
      }  
 
      if (this.sender == null || // is null ?
        this.sender.trim() == "" || // is empty ?
        this.sender.trim().length > 11 || // is more than 11 chars ?
        /^[a-z0-9]+$/i.test(this.sender.trim()) == false || // has any special chars ?
        /^[0-9]+$/i.test(this.sender.trim()) == true) { // is it just numbers ? 
        return lang == "el"       
            ? "O αποστολέας πρέπει να αποτελείται μόνο από γράμματα και να είναι μέχρι 11 χαρακτήρες."
            : "Sender must have only letters and not more than 11 characters"
      }
      
      if (this.body == null || this.body.trim() == "") {
        return lang == "el"       
            ? "Παρακαλώ εισάγετε κείμενο μηνύματος."
            : "Please enter a text for your message."
      }
    }
    else {  // EMAIL
      if (this.recepient == null || this.utilsService.mailIsValid(this.recepient) === false) {
        return lang == "el"       
            ? "To Mail είναι λάθος!"
            : "Mail is wrong!"
      }  
    }

    if (this.message?.messageId == '16' && this.creditCardModeManual === true) {
      const isNullOrEmpty = this.creditCardCharge == null || this.creditCardCharge.toString().trim() == "";
      const isZero = this.creditCardCharge != null && (this.creditCardCharge == 0 || this.creditCardCharge.toString().trim() == "0");
      const isLessThanOne = this.creditCardCharge != null && this.creditCardCharge < 1;
      const hasDecimalPoint = this.creditCardCharge != null && (this.creditCardCharge.toString().trim().indexOf(".") > -1 || this.creditCardCharge.toString().trim().indexOf(",") > -1);
      const notANumber = this.creditCardCharge != null && isNaN(parseInt(this.creditCardCharge.toString()));

      if (isNullOrEmpty === true || isZero === true || isLessThanOne === true || hasDecimalPoint === true || notANumber === true) {
        return lang == "el"       
            ? "To Ποσό χρέωσης είναι λάθος!"
            : "Charge amount is wrong!"
      }

    }
    
    return null;
  }


}
